import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppIcon, IconLabel } from '@/components/ui/app-icon'
import { RefreshCw, Plus, Trash2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui'
import { StatusBadge } from '@/components/ui/status-badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useKAGenDocs, useCreateKAGen, useDeleteKAGen } from '@/hooks/useKAGeneration'
import { kaTemplatesApi } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import {
  collectManualAIs,
  collectAutoAIs,
  aiInputHint,
  autoSourceLabel,
} from '@/lib/ka-template-fields'
import type { KAGenCodeType, KAGenStatus, CreateKAGenDto } from '@/types'
import type { KATemplate } from '@/lib/api'

const CODE_TYPE_LABELS: Record<KAGenCodeType, string> = {
  ka_box: 'Упаковка',
  ka_pallet: 'Паллета',
  ka_box_pallet: 'Упаковка + Паллета',
}

const CODE_TYPE_OPTIONS: { value: KAGenCodeType; label: string }[] = [
  { value: 'ka_box', label: 'Упаковка (КА на ящик)' },
  { value: 'ka_pallet', label: 'Паллета (КА на паллету)' },
  { value: 'ka_box_pallet', label: 'Упаковка + Паллета' },
]

const STATUS_STYLE: Record<KAGenStatus, { label: string; color: string; textColor: string }> = {
  draft: { label: 'Черновик', color: 'bg-gray-100', textColor: 'text-gray-700' },
  generated: { label: 'Сгенерирован', color: 'bg-green-100', textColor: 'text-green-700' },
}

type CreateForm = {
  code_type: KAGenCodeType
  quantity: number
  boxes_per_pallet: number
  ka_template_box: string
  ka_template_pallet: string
  manual_ai_values: Record<string, string>
}

const DEFAULT_FORM: CreateForm = {
  code_type: 'ka_box',
  quantity: 10,
  boxes_per_pallet: 1,
  ka_template_box: 'default_ka_box',
  ka_template_pallet: 'default_ka_pallet',
  manual_ai_values: {},
}

function unwrapTemplates(res: unknown): KATemplate[] {
  if (Array.isArray(res)) return res
  const wrapped = res as { data?: KATemplate[] }
  return wrapped?.data ?? []
}

export function KAGenerationListPage() {
  const navigate = useNavigate()
  const [page] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState<CreateForm>({ ...DEFAULT_FORM })

  const { data, isLoading, isFetching, refetch } = useKAGenDocs(page, 50)
  const createMutation = useCreateKAGen()
  const deleteMutation = useDeleteKAGen()

  const { data: boxTemplatesRes } = useQuery({
    queryKey: ['ka-templates', 'ka_box'],
    queryFn: () => kaTemplatesApi.getByType('ka_box'),
    enabled: createOpen,
  })
  const { data: palletTemplatesRes } = useQuery({
    queryKey: ['ka-templates', 'ka_pallet'],
    queryFn: () => kaTemplatesApi.getByType('ka_pallet'),
    enabled: createOpen,
  })

  const boxTemplates = unwrapTemplates(boxTemplatesRes?.data)
  const palletTemplates = unwrapTemplates(palletTemplatesRes?.data)

  const needsBox = form.code_type === 'ka_box' || form.code_type === 'ka_box_pallet'
  const needsPallet = form.code_type === 'ka_pallet' || form.code_type === 'ka_box_pallet'

  const { data: boxTpl } = useQuery({
    queryKey: ['ka-template', form.ka_template_box],
    queryFn: async () => {
      const res = await kaTemplatesApi.getById(form.ka_template_box)
      return (res.data as KATemplate) ?? null
    },
    enabled: createOpen && needsBox && !!form.ka_template_box,
  })
  const { data: palletTpl } = useQuery({
    queryKey: ['ka-template', form.ka_template_pallet],
    queryFn: async () => {
      const res = await kaTemplatesApi.getById(form.ka_template_pallet)
      return (res.data as KATemplate) ?? null
    },
    enabled: createOpen && needsPallet && !!form.ka_template_pallet,
  })

  const manualAIFields = useMemo(
    () => collectManualAIs([boxTpl, palletTpl]),
    [boxTpl, palletTpl],
  )
  const autoAIFields = useMemo(
    () => collectAutoAIs([boxTpl, palletTpl]),
    [boxTpl, palletTpl],
  )

  const docs = data?.docs ?? []

  const openCreate = () => {
    setForm({ ...DEFAULT_FORM, manual_ai_values: {} })
    setCreateOpen(true)
  }

  const handleCreate = () => {
    const dto: CreateKAGenDto = {
      code_type: form.code_type,
      quantity: form.quantity,
      ka_template_box: needsBox ? form.ka_template_box : undefined,
      ka_template_pallet: needsPallet ? form.ka_template_pallet : undefined,
      manual_ai_values: Object.keys(form.manual_ai_values).length > 0 ? form.manual_ai_values : undefined,
    }
    if (form.code_type === 'ka_box_pallet') {
      dto.boxes_per_pallet = form.boxes_per_pallet
    }
    createMutation.mutate(dto, {
      onSuccess: (res) => {
        setCreateOpen(false)
        const body = res.data as unknown as Record<string, unknown>
        const id = body?.id as string
        if (id) navigate(`/admin/ka-generation/${id}`)
      },
    })
  }

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    if (!confirm('Удалить документ?')) return
    deleteMutation.mutate(id)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            <IconLabel icon="box">Генерация КА</IconLabel>
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Генерация и загрузка КА-кодов для упаковки и паллет</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => refetch()} isLoading={isFetching}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" /> Создать
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      )}

      {!isLoading && docs.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="mb-4 flex justify-center"><AppIcon name="box" className="h-10 w-10" /></div>
          <p className="text-lg">Нет документов КА-генерации</p>
          <p className="text-sm mt-1">Создайте первый документ</p>
        </div>
      )}

      {!isLoading && docs.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Тип</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Кол-во</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Упаковок</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Паллет</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Задание</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {docs.map((doc) => {
                const st = STATUS_STYLE[doc.status] ?? STATUS_STYLE.draft
                return (
                  <tr
                    key={doc.id}
                    onClick={() => navigate(`/admin/ka-generation/${doc.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 font-mono text-sm">{doc.id}</td>
                    <td className="px-4 py-3">
                      <StatusBadge {...st} />
                    </td>
                    <td className="px-4 py-3 text-sm">{CODE_TYPE_LABELS[doc.code_type] ?? doc.code_type}</td>
                    <td className="px-4 py-3 text-sm">{doc.quantity}</td>
                    <td className="px-4 py-3 text-sm">{doc.ka_boxes_count || '—'}</td>
                    <td className="px-4 py-3 text-sm">{doc.ka_pallets_count || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      {doc.linked_task_id ? (
                        <span className="font-mono text-gray-700">{doc.linked_task_id}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDateTime(doc.created_at)}</td>
                    <td className="px-4 py-3">
                      {doc.status === 'draft' && (
                        <button
                          onClick={(e) => handleDelete(e, doc.id)}
                          className="text-red-400 hover:text-red-600 p-1"
                          title="Удалить"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} className="max-w-2xl">
        <Modal.Header onClose={() => setCreateOpen(false)}>Новый документ КА</Modal.Header>
        <Modal.Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Тип КА-кода</label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={form.code_type}
                onChange={(e) => setForm((f) => ({ ...f, code_type: e.target.value as KAGenCodeType }))}
              >
                {CODE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {form.code_type === 'ka_box_pallet' ? 'Кол-во паллет' : 'Количество'}
              </label>
              <input
                type="number"
                className="w-full border rounded-md px-3 py-2 text-sm"
                min={1}
                max={10000}
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
              />
            </div>
            {form.code_type === 'ka_box_pallet' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Упаковок в паллете</label>
                <input
                  type="number"
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  min={1}
                  max={1000}
                  value={form.boxes_per_pallet}
                  onChange={(e) => setForm((f) => ({ ...f, boxes_per_pallet: parseInt(e.target.value) || 1 }))}
                />
              </div>
            )}
            {needsBox && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Шаблон КА упаковки</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={form.ka_template_box}
                  onChange={(e) => setForm((f) => ({ ...f, ka_template_box: e.target.value }))}
                >
                  {boxTemplates.map((t) => (
                    <option key={t.template_id} value={t.template_id}>{t.template_name}</option>
                  ))}
                </select>
              </div>
            )}
            {needsPallet && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Шаблон КА паллеты</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={form.ka_template_pallet}
                  onChange={(e) => setForm((f) => ({ ...f, ka_template_pallet: e.target.value }))}
                >
                  {palletTemplates.map((t) => (
                    <option key={t.template_id} value={t.template_id}>{t.template_name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {autoAIFields.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {autoAIFields.map((entry) => (
                <span
                  key={entry.ai}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 text-xs border border-blue-100"
                >
                  <span className="font-mono">({entry.ai})</span>
                  <span>{entry.label}</span>
                  <span className="text-blue-600 font-medium">{autoSourceLabel(entry.source)}</span>
                </span>
              ))}
            </div>
          )}

          {manualAIFields.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Значения AI (ручной ввод)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {manualAIFields.map((entry) => (
                  <div key={entry.ai}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ({entry.ai}) {entry.label}
                      {entry.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded-md px-3 py-2 text-sm font-mono"
                      value={form.manual_ai_values[entry.ai] ?? entry.default_value ?? ''}
                      placeholder={aiInputHint(entry.ai) || 'Значение'}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          manual_ai_values: { ...f.manual_ai_values, [entry.ai]: e.target.value },
                        }))
                      }
                    />
                    {aiInputHint(entry.ai) && (
                      <p className="text-xs text-gray-400 mt-0.5">{aiInputHint(entry.ai)}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setCreateOpen(false)}>Отмена</Button>
          <Button onClick={handleCreate} isLoading={createMutation.isPending}>Создать</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
